import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faPenToSquare, faCircleMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getAllPartners, deletePartner } from "../../api/Partners";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from 'react-hot-toast';
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import DataTable from "../../components/DataTable";
import TdLink from "../../components/TdLink";
import { useTranslation } from "react-i18next";

const PartnersPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState('');
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", confirm: () => { } });
  const navigate = useNavigate();
  const [partnersLoaded, setPartnersLoaded] = useState(false);

  useEffect(() => {
    handleGetPartners();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const handleGetPartners = async () => {
    try {
      const accessToken = await getAccessToken();
      const newPartners = await getAllPartners(accessToken);

      setPartners(newPartners);
      setPartnersLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeletePartner = async (partnerPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.partner") }));
    try {
      const accessToken = await getAccessToken();
      await deletePartner(partnerPublicId, accessToken);
      const newPartners = await getAllPartners(accessToken);
      setPartners(newPartners);  // Update partners after deletion
      toast.success(t("toast.deleted", { entity: t("entity.partner") }), { id: toastId });
    } catch (error) {
      toast.error(t("toast.failedDelete", { entity: t("entity.partner") }), { id: toastId });
      console.error(error);
    }
  };

  const columns = [
    {
      key: "partnerPublicId",
      header: t("table.partnerId"),
      sortable: true,
      rawCell: true,
      getSortValue: (partner) => partner.partnerPublicId ?? "",
      render: (partner) => (
        <TdLink to={`/partner/${partner.partnerPublicId}`}>
          {partner.partnerPublicId || ""}
        </TdLink>
      ),
    },
    {
      key: "name",
      header: t("common.name"),
      sortable: true,
      rawCell: true,
      getSortValue: (partner) => partner.name ?? "",
      render: (partner) => (
        <TdLink to={`/partner/${partner.partnerPublicId}`}>
          {partner.name || ""}
        </TdLink>
      ),
    },
    {
      key: "actions",
      header: "",
      sortable: false,
      searchable: false,
      rawCell: true,
      headerClassName: "min-w-[64px]",
      render: (partner) => (
        <td>
          <div className="flex justify-end">
            <CustomDropdown
              isRight={true}
              ContentComponent={() => {
                return (
                  <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                    <button
                      className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                      onClick={() => { navigate(`/partner/${partner.partnerPublicId}`) }}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                    </button>
                    <button
                      className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                      onClick={() => { setConfirmState({ isOpen: true, title: t("confirm.deleteEntity", { entity: t("entity.partner"), name: partner.name }), confirm: () => { handleDeletePartner(partner.partnerPublicId) } }) }}
                    >
                      <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                    </button>
                  </div>
                );
              }}
            >
              <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                <FontAwesomeIcon icon={faEllipsis} />
              </div>
            </CustomDropdown>
          </div>
        </td>
      ),
    },
  ];

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">

            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.partners")} loading={!partnersLoaded} />
            </div>

            <div className={`${!partnersLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.partners")}</h2>
                <button className="btn btn-primary" onClick={() => { navigate(`/partner/create`) }}>
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createPartner")}
                </button>
              </div>
              <div className="rounded-md shadow-md w-full p-4">
                <DataTable
                  rows={partners}
                  columns={columns}
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder={t("search.partner")}
                  defaultSortColumn="partnerPublicId"
                  rowKey={(partner, index) => partner.partnerPublicId || index}
                  emptyColSpan={3}
                  emptyContent={t("common.noEntityFound", { entity: t("entity.partners") })}
                />
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => { setConfirmState((s) => ({ ...s, isOpen: false })) }}
        onConfirm={() => { setConfirmState((s) => ({ ...s, isOpen: false })); confirmState.confirm() }}
      />
    </>
  );
};

export default PartnersPage;
